import Vue from 'vue'
import Vuex from 'vuex'
import AuthService from '@/services/AuthService'
import LoginService from '@/services/LoginService'
import {
  TYPE_PHONE,
  SMS_PIN_WARNING_THRESHOLD,
  VALIDATION_STATUS_ABUSED,
  TWOFACTOR_TYPE,
  STATUS_NOT_READY,
  STATUS_LOADING,
  STATUS_READY,
  STATUS_ERROR
} from '@/constants'

import {
  PIN_SENT,
  RECOVER_FINISHED,
  RECEIVED_COUNTRY_PREFIXES,
  SET_REDIRECT_URI,
  SHOW_MODAL,
  HIDE_MODAL,
  UPDATE_HAS_TEMP_PASSWORD,
  SET_2FA_VALIDATION_SESSION,
  SET_2FA_PROXY,
  FETCH_API_NONCE_REQUEST,
  FETCH_API_NONCE_SUCCESS,
  FETCH_API_NONCE_FAILURE
} from '@/store/mutationTypes'

Vue.use(Vuex)

export default new Vuex.Store({
  state: {
    recover: null,
    sentSMSPinCount: 0,
    smsPinAbused: false,
    countryPrefixes: [],
    currentCountryPrefix: {},
    redirectUri: null,
    modalVisible: false,
    modalComponent: null,
    modalProps: {},
    hasTempPassword: false,
    apiNonce: null,
    apiNonceFetchedAt: null,
    fetchApiNonceStatus: STATUS_NOT_READY,
    twoFactorValidationSession: sessionStorage.getItem('twoFactorValidationSession'),
    twoFactorProxy: JSON.parse(sessionStorage.getItem('twoFactorProxy') || 'null')
  },
  mutations: {
    [PIN_SENT] (state, recoverNewState) {
      state.recover = recoverNewState
    },
    'INCREMENT_SENT_SMS_PIN_COUNT' (state) {
      state.sentSMSPinCount++
    },
    [RECOVER_FINISHED] (state) {
      state.recover = null
    },
    [RECEIVED_COUNTRY_PREFIXES] (state, countryPrefixes) {
      state.countryPrefixes = countryPrefixes
    },
    'SET_CURRENT_COUNTRY_PREFIX' (state, countryPrefix) {
      state.currentCountryPrefix = countryPrefix
    },
    'SET_SMS_PIN_ABUSED' (state) {
      state.smsPinAbused = true
    },
    [SET_REDIRECT_URI] (state, newUri) {
      state.redirectUri = newUri
    },
    [SHOW_MODAL] (state, { componentName, props }) {
      Vue.set(state, 'modalProps', props)
      state.modalVisible = true
      state.modalComponent = componentName
    },
    [HIDE_MODAL] (state) {
      state.modalVisible = false
    },
    [UPDATE_HAS_TEMP_PASSWORD] (state, value) {
      state.hasTempPassword = value
    },
    [SET_2FA_VALIDATION_SESSION] (state, session) {
      state.twoFactorValidationSession = session
      if (session) {
        sessionStorage.setItem('twoFactorValidationSession', session)
      } else {
        sessionStorage.removeItem('twoFactorValidationSession')
      }
    },
    [SET_2FA_PROXY] (state, proxy) {
      state.twoFactorProxy = proxy
      if (proxy) {
        sessionStorage.setItem('twoFactorProxy', JSON.stringify(proxy))
      } else {
        sessionStorage.removeItem('twoFactorProxy')
      }
    },
    [FETCH_API_NONCE_REQUEST] (state) {
      state.fetchApiNonceStatus = STATUS_LOADING
    },
    [FETCH_API_NONCE_SUCCESS] (state, nonce) {
      state.apiNonce = nonce
      state.apiNonceFetchedAt = Date.now()
      state.fetchApiNonceStatus = STATUS_READY
    },
    [FETCH_API_NONCE_FAILURE] (state) {
      // the previous nonce is left in place: it may still be valid, and a
      // stale-but-present nonce beats none at all for the next submit
      state.fetchApiNonceStatus = STATUS_ERROR
    }
  },
  getters: {
    showSMSPinWarning (state) {
      return state.sentSMSPinCount >= SMS_PIN_WARNING_THRESHOLD
    }
  },
  actions: {
    async recoverPassword ({ dispatch }, { type, value }) {
      const guid = await AuthService.checkProxies({ type, value })
      return await dispatch('sendPin', { type, value, guid })
    },
    async sendPin ({ commit }, { type, value, guid, resend }) {
      const res = await AuthService.sendValidation({ type, value, guid, resend })
      if (res.sent === VALIDATION_STATUS_ABUSED) {
        commit('SET_SMS_PIN_ABUSED')
      }
      commit(PIN_SENT, { type, value, guid })
      if (type === TYPE_PHONE) {
        commit('INCREMENT_SENT_SMS_PIN_COUNT')
      }
    },
    async resend2FAPin ({ state, dispatch }) {
      return await dispatch('sendPin', {
        type: TWOFACTOR_TYPE,
        guid: state.twoFactorValidationSession,
        resend: true
      })
    },
    async checkValidation ({ state }, pin) {
      return await AuthService.checkValidation({
        type: state.recover.type,
        value: state.recover.value,
        guid: state.recover.guid,
        code: pin
      })
    },
    async validationAction ({ state }, { password, fingerprint }) {
      return await AuthService.validationAction({
        guid: state.recover.guid,
        password,
        fingerprint
      })
    },
    async getCountryPrefixes ({ commit }) {
      const countryPrefixes = await AuthService.getCountryPrefixes()
      commit(RECEIVED_COUNTRY_PREFIXES, countryPrefixes)
    },
    goToRedirectUri ({ state }) {
      if (state.redirectUri) {
        window.location.href = state.redirectUri
      }
    },
    cleanRecover ({ commit }) {
      commit(RECOVER_FINISHED)
    },
    async validateTwoFactorPin ({ state, commit }, { code, fingerprint }) {
      return await LoginService.twoFactorValidation({
        guid: state.twoFactorValidationSession,
        code,
        fingerprint
      }, state.apiNonce).then(function (res) {
        commit(SET_2FA_VALIDATION_SESSION, null)
        commit(SET_2FA_PROXY, null)
        return res
      })
    },
    async fetchApiNonce ({ state, commit }) {
      // Several triggers can fire at once (page mount, the refresh timer, the
      // visibility catch-up, the error interceptor in main.js). Skipping while
      // one is in flight keeps them from committing each other's nonce out of
      // order, and stops a failing fetch from looping through the interceptor.
      if (state.fetchApiNonceStatus === STATUS_LOADING) return
      commit(FETCH_API_NONCE_REQUEST)
      try {
        const nonce = await AuthService.getNonce()
        commit(FETCH_API_NONCE_SUCCESS, nonce)
        return nonce
      } catch (err) {
        commit(FETCH_API_NONCE_FAILURE)
        throw err
      }
    }
  }
})
